import { LitElement, html, css, unsafeHTML } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Bundle Plan Picker Component
 * Adapted from Skio Plan Picker for Dynamic Box API
 *
 * Uses LitJS (<5kb) https://lit.dev/ for reactive web components
 */

export class BundlePlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    // Dynamic Box data
    sellingPlanGroup: { type: Object },

    // Price data (calculated by parent)
    bundleTotal: { type: Number },
    bundleDiscountedTotal: { type: Number },
    bundleComplete: { type: Boolean },

    // Configuration options
    options: { type: Object },

    // State
    availableSellingPlans: { state: true },
    selectedSellingPlan: { type: Object },
    isSubscription: { type: Boolean },

    debug: { type: Boolean },
  }

  static styles = [
    css`
      :host {
        display: block !important; /* Override *:empty { display: none } */
        width: 100%;
      }

      .skio-plan-picker {
        display: flex;
        gap: 1rem;
        padding: 0;
        border: 0;
        font-size: 14px;
        color: black;
      }

      :host([layout='hidden']) .skio-plan-picker {
        display: none;
      }

      :host([layout='vertical']) .skio-plan-picker {
        flex-direction: column;
      }

      :host([layout='horizontal']) .skio-plan-picker {
        flex-direction: row;
        flex-wrap: wrap;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      .group-container {
        display: block;
        position: relative;
        transition: border-color 0.2s ease;
        border: var(--skio-group-border-width, 1px) solid var(--skio-group-border-color, #ccc);
        border-radius: var(--skio-group-border-radius, 0);
        background-color: var(--skio-group-background-color, transparent);
        color: var(--skio-group-text-color, #000);
      }

      .group-container--selected {
        border-color: var(--skio-group-border-selected-color, #333);
        background-color: var(--skio-group-background-selected-color, transparent);
        color: var(--skio-group-text-selected-color, #000);
        background-image: var(--skio-group-background-selected-image);
        background-size: cover;
        background-position: 50% 20%;
      }

      .group-container--last {
        order: 1;
      }

      :host([layout='horizontal']) .group-container {
        width: calc(50% - 0.5rem - calc(2 * var(--skio-group-border-width, 1px)));
      }

      .group-input {
        position: absolute;
        opacity: 0;
        width: 0px;
        height: 0px;
      }

      .group-input:focus-visible ~ .group-label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-label {
        display: flex;
        flex-direction: column;
        padding: 10px;
        cursor: pointer;
      }

      .group-topline {
        display: flex;
        align-items: center;
        width: 100%;
        gap: 8px;
      }

      :host([layout='horizontal']) .group-topline {
        flex-direction: column;
        text-align: center;
      }

      .skio-radio__container {
        display: flex;
        color: var(--skio-group-border-color, #333);
      }

      .group-container--selected .skio-radio__container {
        color: var(--skio-group-border-selected-color, #333);
      }

      .skio-radio {
        transform-origin: center;
        opacity: 0;
        transform: scale(0);
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-label:hover .skio-radio {
        opacity: 0.75;
        transform: scale(1);
      }

      .group-container--selected .group-label .skio-radio {
        opacity: 1;
        transform: scale(1);
      }

      .skio-price {
        display: flex;
        justify-content: flex-end;
        flex-wrap: wrap;
        align-items: center;
        text-align: right;
        gap: 4px;
        line-height: 1;
        margin-left: auto;
        vertical-align: middle;
        font-weight: 500;
        font-size: 20px;
      }

      .skio-price s {
        font-size: 18px;
        font-weight: 400;
        opacity: 0.5;
      }

      .skio-price span {
        font-size: 11px;
      }

      :host([layout='horizontal']) .skio-price {
        text-align: center;
        margin-right: auto;
      }

      .group-content {
        display: flex;
        flex-direction: column;
        opacity: 1;
        width: auto;
        max-height: 1000px;
        transition:
          max-height 0.15s cubic-bezier(0.4, 0, 0.2, 1),
          opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-content.margin-left {
        margin-left: 25px;
      }

      .group-content p {
        margin: 0.5rem 0 0 0;
        font-size: 13px;
        color: #000;
      }

      :host([layout='horizontal']) .group-content {
        width: 100%;
      }

      /* Hide frequency if not selected */
      .group-container:not(.group-container--selected) .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      :host([layout='horizontal']) .group-container:not(.group-container--selected) + .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      .group-title {
        width: 100%;
        max-width: 80%;
        line-height: 1.5;
        font-family: "Font2";
        font-size: 16px;
      }

      :host([layout='horizontal']) .group-title {
        max-width: 100%;
      }

      .savings {
        font-size: 16px;
        white-space: nowrap;
      }

      .selling-plan-dropdown {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        display: flex;
        align-items: center;
        text-decoration: underline;
        gap: 5px;
        width: 95%;
        margin-top: 5px;
        margin-bottom: 10px;
        padding: 10px 30px 10px 10px;
        background-color: transparent;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: left 174px top 50%;
        background-size: 19px;
        border: 2px solid;
        border-radius: 20px;
        font-size: 15px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .selling-plan-dropdown--one {
        background-image: none;
        pointer-events: none;
        background: transparent;
      }

      .selling-plan-dropdown:focus {
        outline: none;
      }

      .selling-plan-buttons {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(var(--skio-button-plan-selector-width, 20px), 1fr));
        gap: 10px;
        padding: 0.75rem 0;
        border: 0;
        font-size: 13px;
      }

      .selling-plan-buttons input[type='radio'] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .selling-plan-buttons label {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border: 1px solid #ccc;
        text-align: center;
        padding: 1rem 0.5rem;
      }

      .selling-plan-buttons input[type='radio']:checked + label {
        border-color: #000;
      }

      .selling-plan-buttons input:focus-visible + label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .benefits-text {
        font-size: 14px;
        line-height: 1.8;
        color: var(--skio-group-text-color, #333);
      }

      @media screen and (min-width: 750px) {
        .skio-price {
          font-size: 24px;
        }

        .group-title {
          font-size: 16px;
        }
      }
    `,
  ]

  constructor() {
    super()

    this.currency = window.Shopify?.currency?.active || 'USD'
    this.language = window.Shopify?.locale || 'en-US'
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

    this.isSubscription = false
    this.selectedSellingPlan = null
    this.availableSellingPlans = []
    this.debug = window?.Shopify?.designMode || false
  }

  connectedCallback() {
    super.connectedCallback()
    this.processSellingPlans()
  }

  updated(changed) {
    if (changed.has('sellingPlanGroup')) {
      this.processSellingPlans()
    }

    if (changed.has('selectedSellingPlan') || changed.has('isSubscription')) {
      this.dispatchSellingPlanEvent()
    }
  }

  // Transform Dynamic Box sellingPlanGroup to internal format
  processSellingPlans() {
    if (!this.sellingPlanGroup || Object.keys(this.sellingPlanGroup).length === 0) {
      this.availableSellingPlans = []
      return
    }

    this.availableSellingPlans = Object.entries(this.sellingPlanGroup).map(([planId, plan]) => ({
      id: planId,
      name: this.formatIntervalText(plan.interval, plan.intervalCount),
      title: plan.title,
      discountPercent: plan.priceAdjustmentType === 'PERCENTAGE' ? plan.priceAdjustmentAmount : 0,
      discountAmount: plan.priceAdjustmentType === 'FIXED_AMOUNT' ? plan.priceAdjustmentAmount : 0,
      interval: plan.interval,
      intervalCount: plan.intervalCount,
    }))

    // Set default selection based on options
    if (this.options?.start_onetime !== false) {
      this.isSubscription = false
      this.selectedSellingPlan = null
    } else if (this.availableSellingPlans.length > 0) {
      this.isSubscription = true
      this.selectedSellingPlan = this.availableSellingPlans[0]
    }
  }

  formatIntervalText(interval, count) {
    const intervalLower = (interval || 'MONTH').toLowerCase()
    if (count === 1) {
      return `Delivery every ${intervalLower === 'week' ? 'week' : intervalLower === 'month' ? 'month' : intervalLower}`
    }
    const plural = intervalLower === 'week' ? 'weeks' : intervalLower === 'month' ? 'months' : `${intervalLower}s`
    return `Delivery every ${count} ${plural}`
  }

  // Templates
  radioTemplate() {
    return html`
      <div class="skio-radio__container">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1"></circle>
          <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
        </svg>
      </div>
    `
  }

  sellingPlanDropdown() {
    if (this.availableSellingPlans.length === 0) return ''

    return html`
      <select
        class="selling-plan-dropdown ${this.availableSellingPlans.length === 1 ? 'selling-plan-dropdown--one' : ''}"
        @change=${e => this.selectSellingPlan(e.target.value)}
      >
        ${this.availableSellingPlans.map(
          plan => html`
            <option ?selected=${this.selectedSellingPlan?.id === plan.id} value=${plan.id}>
              ${plan.name}
            </option>
          `
        )}
      </select>
    `
  }

  sellingPlanButtons() {
    if (this.availableSellingPlans.length === 0) return ''

    return html`
      <fieldset class="selling-plan-buttons">
        <legend class="sr-only">Select subscription interval</legend>
        ${this.availableSellingPlans.map(
          plan => html`
            <input
              type="radio"
              name="selling_plan_button"
              value="${plan.id}"
              id="selling_plan_button-${plan.id}"
              @change="${() => this.selectSellingPlan(plan.id)}"
              ?checked=${this.selectedSellingPlan?.id === plan.id}
            />
            <label for="selling_plan_button-${plan.id}">${plan.name}</label>
          `
        )}
      </fieldset>
    `
  }

  groupContent() {
    const showRadio = this.options?.show_radio_selector !== false
    const layout = this.options?.layout || 'vertical'

    return html`
      <div class="group-content ${layout !== 'horizontal' && showRadio ? 'margin-left' : ''}">
        ${this.options?.selector_type === 'button' ? this.sellingPlanButtons() : this.sellingPlanDropdown()}
        ${this.options?.additional_subscription_content
          ? html`<div class="benefits-text">${unsafeHTML(this.options.additional_subscription_content)}</div>`
          : html`<div class="benefits-text">
              ✓ Easy to pause, skip, or swap<br>
              ✓ Cancel any time
            </div>`
        }
      </div>
    `
  }

  render() {
    const hasSubscriptions = this.availableSellingPlans.length > 0
    const layout = this.options?.layout || 'vertical'
    const showRadio = this.options?.show_radio_selector !== false
    const onetimeFirst = this.options?.onetime_first !== false

    if (!hasSubscriptions) {
      return ''
    }

    // Get discount percent for display - use override if set, otherwise use first plan's discount
    const subscriptionOverride = this.options?.subscription_discount_override || 0
    const firstPlan = this.availableSellingPlans[0]
    const discountPercent = subscriptionOverride > 0 ? subscriptionOverride : (firstPlan?.discountPercent || 0)

    return html`
      <fieldset class="skio-plan-picker" role="radiogroup" aria-labelledby="bundle-plan-picker-legend">
        <legend id="bundle-plan-picker-legend" class="sr-only">Select purchase type</legend>

        <!-- One-time Purchase Option -->
        <div
          class="group-container ${!this.isSubscription ? 'group-container--selected' : ''} ${!onetimeFirst ? 'group-container--last' : ''}"
          @click=${() => this.selectPurchaseType(false)}
        >
          <input
            id="one-time-${this.key}"
            class="group-input"
            name="bundle-group-${this.key}"
            type="radio"
            value="onetime"
            ?checked=${!this.isSubscription}
            @change=${() => this.selectPurchaseType(false)}
          />
          <label class="group-label" for="one-time-${this.key}">
            <div class="group-topline">
              ${layout !== 'horizontal' && showRadio ? this.radioTemplate() : ''}
              <div class="group-title">${this.options?.onetime_title || 'One time purchase'}</div>
              <div class="skio-price" aria-live="polite">
                ${this.bundleComplete
                  ? this.bundleTotal > this.bundleDiscountedTotal
                    ? html`
                        <s aria-hidden="true">${this.money(this.bundleTotal)}</s>
                        ${this.money(this.bundleDiscountedTotal || 0)}
                      `
                    : this.money(this.bundleDiscountedTotal || 0)
                  : ''}
              </div>
            </div>
          </label>
        </div>

        <!-- Subscription Option -->
        ${hasSubscriptions
          ? html`
              <div
                class="group-container ${this.isSubscription ? 'group-container--selected' : ''}"
                @click=${() => this.selectPurchaseType(true)}
              >
                <input
                  id="subscription-${this.key}"
                  class="group-input"
                  name="bundle-group-${this.key}"
                  type="radio"
                  value="subscription"
                  ?checked=${this.isSubscription}
                  @change=${() => this.selectPurchaseType(true)}
                />
                <label class="group-label" for="subscription-${this.key}">
                  <div class="group-topline">
                    ${layout !== 'horizontal' && showRadio ? this.radioTemplate() : ''}
                    <div class="group-title">
                      ${this.options?.subscription_title || 'Subscribe'}
                      ${discountPercent > 0
                        ? html`<span class="savings">${discountPercent}%</span>`
                        : ''}
                    </div>
                    <div class="skio-price" aria-live="polite">
                      ${this.bundleComplete
                        ? html`
                            <s aria-hidden="true">${this.money(this.getSubscriptionComparePrice())}</s>
                            ${this.money(this.calculateSubscriptionPrice())}
                          `
                        : ''}
                    </div>
                  </div>
                  ${layout === 'vertical' ? this.groupContent() : ''}
                </label>
              </div>
              ${layout === 'horizontal' ? this.groupContent() : ''}
            `
          : ''}
      </fieldset>
    `
  }

  // Calculate subscription price with additional discount
  calculateSubscriptionPrice() {
    if (!this.bundleDiscountedTotal) {
      return 0
    }

    // Check for subscription discount override (applies % to original price, not tier-discounted)
    const subscriptionOverride = this.options?.subscription_discount_override || 0
    if (subscriptionOverride > 0 && this.bundleTotal) {
      return this.bundleTotal * (1 - subscriptionOverride / 100)
    }

    // Default behavior: apply selling plan discount to tier-discounted price
    const plan = this.selectedSellingPlan || this.availableSellingPlans[0]
    if (!plan) {
      return this.bundleDiscountedTotal
    }

    if (plan.discountPercent > 0) {
      return this.bundleDiscountedTotal * (1 - plan.discountPercent / 100)
    } else if (plan.discountAmount > 0) {
      return this.bundleDiscountedTotal - plan.discountAmount
    }

    return this.bundleDiscountedTotal
  }

  // Get the compare price for subscription (shown crossed out)
  getSubscriptionComparePrice() {
    // When using override, compare against original price since that's what the discount is based on
    const subscriptionOverride = this.options?.subscription_discount_override || 0
    if (subscriptionOverride > 0 && this.bundleTotal) {
      return this.bundleTotal
    }
    // Default: compare against tier-discounted price
    return this.bundleDiscountedTotal || 0
  }

  // Event handlers
  selectPurchaseType(isSubscription) {
    this.isSubscription = isSubscription

    if (isSubscription && this.availableSellingPlans.length > 0) {
      if (!this.selectedSellingPlan) {
        this.selectedSellingPlan = this.availableSellingPlans[0]
      }
    } else {
      // Keep the plan selected but mark as not subscription
    }

    this.requestUpdate()
  }

  selectSellingPlan(planId) {
    const plan = this.availableSellingPlans.find(p => p.id === planId)
    if (plan) {
      this.selectedSellingPlan = plan
      this.isSubscription = true
      this.requestUpdate()
    }
  }

  dispatchSellingPlanEvent() {
    const event = new CustomEvent('skio::update-selling-plan', {
      bubbles: true,
      composed: true,
      detail: {
        sellingPlanId: this.isSubscription ? this.selectedSellingPlan?.id : null,
        sellingPlan: this.isSubscription ? this.selectedSellingPlan : null,
        isSubscription: this.isSubscription,
        key: this.key,
      },
    })
    this.dispatchEvent(event)
  }

  // Utility
  money(price) {
    // Price is already in dollars, not cents
    return this.moneyFormatter.format(price)
  }

  // Public method to get current selection
  getSelectedPlan() {
    return {
      isSubscription: this.isSubscription,
      sellingPlanId: this.isSubscription ? this.selectedSellingPlan?.id : null,
      sellingPlan: this.isSubscription ? this.selectedSellingPlan : null,
    }
  }
}

if (!customElements.get('bundle-plan-picker')) {
  customElements.define('bundle-plan-picker', BundlePlanPicker)
}

if (window) window.BundlePlanPicker = BundlePlanPicker
